import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next'; // Ավելացված է
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faTrash, faPlus, faEdit, faImage, faSave, 
    faProjectDiagram, faCheckCircle, faTimes, faCloudUploadAlt 
} from '@fortawesome/free-solid-svg-icons';
import { API_BASE } from '../apiConfig';

const AdminPortfolio = ({ list, refresh }) => {
    const { t } = useTranslation(); // Ավելացված է
    const [formData, setFormData] = useState({ name: '', description: '' });
    const [files, setFiles] = useState(null);
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(false);

    const getAuthHeaders = useCallback(() => {
        const token = localStorage.getItem('token');
        return {
            'Authorization': `Bearer ${token}`,
            'ngrok-skip-browser-warning': 'true'
        };
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const data = new FormData();
            data.append('name', formData.name);
            data.append('description', formData.description || '');

            if (files) {
                Array.from(files).forEach(f => data.append('images', f));
            }

            const method = editId ? 'PUT' : 'POST';
            const url = editId ? `${API_BASE}/api/portfolio/${editId}` : `${API_BASE}/api/portfolio`;

            const res = await fetch(url, {
                method: method,
                headers: getAuthHeaders(),
                body: data
            });

            if (res.status === 401) {
                alert(t('error_session_expired'));
                return;
            }

            if (res.ok) {
                setFormData({ name: '', description: '' });
                setFiles(null);
                setEditId(null);
                refresh();
            }
        } catch (err) {
            console.error("Portfolio error:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (item) => {
        setEditId(item.id);
        setFormData({ name: item.name, description: item.description });
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const deleteItem = async (id) => {
        if (!window.confirm(t('confirm_delete_project'))) return;
        try {
            const res = await fetch(`${API_BASE}/api/portfolio/${id}`, { 
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (res.ok) {
                refresh();
            } else if (res.status === 401) {
                alert(t('error_access_denied'));
            }
        } catch (err) {
            console.error("Delete error:", err);
        }
    };

    return (
        <div style={styles.container}>
            {/* ANALYTIC CARDS */}
            <div style={styles.analyticsRow}>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faProjectDiagram} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('total_projects')}</p>
                        <h3 style={styles.anaVal}>{list.length}</h3>
                    </div>
                </div>
                <div style={{...styles.anaCard, background: 'linear-gradient(135deg, #01B574 0%, #2AF598 100%)'}}>
                    <div style={styles.anaIconBox}><FontAwesomeIcon icon={faCheckCircle} /></div>
                    <div>
                        <p style={styles.anaLabel}>{t('status_label')}</p>
                        <h3 style={styles.anaVal}>{t('status_active')}</h3>
                    </div>
                </div>
            </div>

            {/* FORM CARD */}
            <div style={styles.card}>
                <div style={styles.cardHeader}>
                    <div style={styles.iconBox}>
                        <FontAwesomeIcon icon={editId ? faEdit : faPlus} />
                    </div>
                    <h3 style={styles.cardTitle}>
                        {editId ? t('edit_project_title') : t('add_project_title')}
                    </h3>
                </div>

                <form onSubmit={handleSubmit} style={styles.form}>
                    <div style={styles.gridInputs}>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('project_name_label')}</label>
                            <input 
                                style={styles.input}
                                placeholder={t('project_name_placeholder')} 
                                value={formData.name} 
                                onChange={e => setFormData({...formData, name: e.target.value})} 
                                required 
                            />
                        </div>
                        <div style={styles.inputGroup}>
                            <label style={styles.label}>{t('images_label')}</label>
                            <label style={styles.fileLabel}>
                                <FontAwesomeIcon icon={faCloudUploadAlt} style={{marginRight: '10px'}} />
                                {files ? `${files.length} ${t('files_selected_suffix')}` : t('upload_images_hint')}
                                <input type="file" multiple hidden onChange={e => setFiles(e.target.files)} />
                            </label>
                        </div>
                    </div>

                    <div style={styles.inputGroup}>
                        <label style={styles.label}>{t('description_label')}</label>
                        <textarea 
                            style={styles.textarea}
                            placeholder={t('description_placeholder')} 
                            value={formData.description} 
                            onChange={e => setFormData({...formData, description: e.target.value})} 
                            rows="3"
                        />
                    </div>

                    <div style={styles.btnRow}>
                        <button type="submit" style={styles.saveBtn} disabled={loading}>
                            <FontAwesomeIcon icon={faSave} style={{marginRight: '8px'}} />
                            {loading ? t('btn_processing') : (editId ? t('btn_update') : t('btn_publish'))}
                        </button>
                        {editId && (
                            <button type="button" onClick={() => {setEditId(null); setFormData({name:'', description:''})}} style={styles.cancelBtn}>
                                <FontAwesomeIcon icon={faTimes} /> {t('btn_cancel')}
                            </button>
                        )}
                    </div>
                </form>
            </div>

            {/* LIST TABLE CARD */}
            <div style={{...styles.card, marginTop: '30px'}}>
                <h3 style={{...styles.cardTitle, marginBottom: '20px'}}>{t('projects_list_title')}</h3>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={styles.thRow}>
                                <th style={styles.th}>{t('table_col_image')}</th>
                                <th style={styles.th}>{t('table_col_name')}</th>
                                <th style={styles.th}>{t('table_col_description')}</th>
                                <th style={{...styles.th, textAlign: 'center'}}>{t('table_col_actions')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {list.map(item => (
                                <tr key={item.id} style={styles.tr}>
                                    <td style={styles.td}>
                                        <div style={styles.imgContainer}>
                                            {item.image ? (
                                               <img 
    src={item.image ? `${API_BASE}/uploads/${item.image.split(',')[0].trim()}` : '/assets/img/placeholder.png'} 
    alt={item.name || "Preview"} 
    style={styles.previewImg}
    loading="lazy"      // 1. Բեռնել միայն այն ժամանակ, երբ սքրոլ անելիս հասնում ես սրան
    decoding="async"    // 2. Նկարի վերծանումը թողնել ֆոնային ռեժիմում
    onError={(e) => { 
        e.target.onerror = null; 
        e.target.src = '/assets/img/placeholder.png'; 
    }} 
/>
                                            ) : (
                                                <div style={styles.noImg}><FontAwesomeIcon icon={faImage} /></div>
                                            )}
                                        </div>
                                    </td>
                                    <td style={styles.tdName}>{item.name}</td>
                                    <td style={styles.tdDesc}>{item.description?.substring(0, 50)}...</td>
                                    <td style={styles.tdActions}>
                                        <button onClick={() => handleEdit(item)} style={styles.editBtn}>
                                            <FontAwesomeIcon icon={faEdit} />
                                        </button>
                                        <button onClick={() => deleteItem(item.id)} style={styles.deleteBtn}>
                                            <FontAwesomeIcon icon={faTrash} />
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

const styles = {
    container: { fontFamily: "'Plus Jakarta Sans', sans-serif" },
    analyticsRow: { display: 'flex', gap: '20px', marginBottom: '30px' },
    anaCard: { flex: 1, padding: '20px', borderRadius: '20px', color: '#fff', display: 'flex', alignItems: 'center', gap: '15px' },
    anaIconBox: { width: '40px', height: '40px', borderRadius: '10px', background: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    anaLabel: { fontSize: '11px', margin: 0, opacity: 0.8, textTransform: 'uppercase' },
    anaVal: { fontSize: '20px', margin: 0, fontWeight: '800' },

    card: { background: '#fff', borderRadius: '24px', padding: '25px', boxShadow: '0 10px 30px rgba(0,0,0,0.02)', border: '1px solid #f1f5f9' },
    cardHeader: { display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '25px' },
    iconBox: { width: '40px', height: '40px', borderRadius: '12px', background: '#4318FF', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
    cardTitle: { fontSize: '18px', fontWeight: '800', color: '#1B2559', margin: 0 },

    form: { display: 'flex', flexDirection: 'column', gap: '15px' },
    gridInputs: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
    inputGroup: { display: 'flex', flexDirection: 'column', gap: '8px' },
    label: { fontSize: '12px', fontWeight: '700', color: '#1B2559', marginLeft: '5px' },
    input: { padding: '12px', borderRadius: '12px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none' },
    textarea: { padding: '12px', borderRadius: '12px', border: '1px solid #E0E5F2', background: '#F4F7FE', outline: 'none', resize: 'vertical' },
    fileLabel: { padding: '12px', borderRadius: '12px', border: '2px dashed #E0E5F2', background: '#F4F7FE', color: '#707EAE', cursor: 'pointer', textAlign: 'center', fontSize: '13px' },
    
    btnRow: { display: 'flex', gap: '10px', marginTop: '10px' },
    saveBtn: { flex: 2, background: 'linear-gradient(135deg, #4318FF 0%, #5E37FF 100%)', color: '#fff', border: 'none', padding: '12px', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' },
    cancelBtn: { flex: 1, background: '#F4F7FE', color: '#1B2559', border: 'none', borderRadius: '12px', fontWeight: '700', cursor: 'pointer' },

    tableWrapper: { overflowX: 'auto' },
    table: { width: '100%', borderCollapse: 'collapse' },
    thRow: { borderBottom: '1px solid #F4F7FE' },
    th: { padding: '12px', textAlign: 'left', color: '#A3AED0', fontSize: '11px', fontWeight: '800', textTransform: 'uppercase' },
    tr: { borderBottom: '1px solid #F4F7FE' },
    td: { padding: '12px' },
    tdName: { padding: '12px', fontWeight: '700', color: '#1B2559' },
    tdDesc: { padding: '12px', color: '#707EAE', fontSize: '13px' },
    imgContainer: { width: '50px', height: '50px', borderRadius: '10px', overflow: 'hidden', background: '#F4F7FE' },
    previewImg: { width: '100%', height: '100%', objectFit: 'cover' },
    noImg: { width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#A3AED0' },
    tdActions: { display: 'flex', gap: '8px', justifyContent: 'center', padding: '20px 0' },
    editBtn: { background: '#E0E7FF', color: '#4318FF', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' },
    deleteBtn: { background: '#FEE2E2', color: '#EF4444', border: 'none', width: '32px', height: '32px', borderRadius: '8px', cursor: 'pointer' }
};

export default AdminPortfolio;